import { type KeyboardEvent } from "react";
import type { Calendar, CalendarEvent } from "@/planning/model/wire.js";
import { Button, Field, Modal, cx, inputBase, inputClass } from "@/platform/ui";
import { TextArea, useConfirm, useConfirmedClose } from "@/platform/ui";
import { RecurrenceEditor } from "@/planning/ui/components/RecurrenceEditor.js";
import { ReminderPicker } from "@/planning/ui/components/ReminderPicker.js";
import { useEntityForm } from "@/platform/client";

// 30-minute suggestions for the time fields (the dropdown), while still allowing
// the user to type any value.
const TIME_OPTIONS = Array.from({ length: 48 }, (_, i) => {
  const h = String(Math.floor(i / 2)).padStart(2, "0");
  const m = i % 2 === 0 ? "00" : "30";
  return `${h}:${m}`;
});
import {
  DURATION_OPTIONS,
  addMinutesToDateTime,
  combineDateTime,
  durationLabel,
  isoDateKey,
  minutesBetween,
  splitDateTime,
} from "@/platform/client";
import {
  useCreateEvent,
  useDeleteEvent,
  useEvent,
  useUpdateEvent,
} from "@/planning/ui/queries/calendar.js";
import { useTz } from "@/platform/client";
import {
  emptyRecurrenceForm,
  recurrenceFormFromRule,
  recurrenceFormToRule,
  type RecurrenceForm,
} from "@/planning/ui/lib/recurrenceForm.js";

const DEFAULT_TIME = "09:00";
const DEFAULT_DURATION = 60;
const DEFAULT_REMINDER = 60; // 1 hour before, on a new event

interface FormState {
  title: string;
  calendarId: number;
  location: string;
  description: string;
  allDay: boolean;
  sDate: string;
  sTime: string;
  eDate: string;
  eTime: string;
  reminders: number[];
  recur: RecurrenceForm;
}

// A timed event is edited in its OWN origin zone (so "09:00 Paris" reads and
// saves as 09:00 even when the home zone is elsewhere); a new event's origin is
// the home zone. See docs/timezones.md.
function editZone(editing: CalendarEvent | null, tz: string): string {
  return editing?.timezone || tz;
}

function initialForm(
  editing: CalendarEvent | null,
  day: Date,
  calendars: Calendar[],
  tz: string,
): FormState {
  if (editing) {
    const zone = editZone(editing, tz);
    // All-day events are zone-less: their date is the stored UTC Y-M-D read
    // verbatim, never converted through a zone (docs/timezones.md). Timed events
    // are instants, split into their origin-zone wall-clock fields.
    const s = editing.allDay
      ? { date: editing.start.slice(0, 10), time: "00:00" }
      : splitDateTime(editing.start, zone);
    const e = editing.allDay
      ? { date: (editing.end ?? editing.start).slice(0, 10), time: "00:00" }
      : editing.end
        ? splitDateTime(editing.end, zone)
        : addMinutesToDateTime(s.date, s.time, DEFAULT_DURATION);
    return {
      title: editing.title,
      calendarId: editing.calendarId,
      location: editing.location ?? "",
      description: editing.description ?? "",
      allDay: editing.allDay,
      sDate: s.date,
      sTime: s.time,
      eDate: e.date,
      eTime: e.time,
      reminders: editing.reminders ?? [],
      recur: recurrenceFormFromRule(editing.recurrenceRule),
    };
  }
  const sDate = isoDateKey(day);
  const end = addMinutesToDateTime(sDate, DEFAULT_TIME, DEFAULT_DURATION);
  return {
    title: "",
    calendarId: calendars[0]?.id ?? 1,
    location: "",
    description: "",
    allDay: false,
    sDate,
    sTime: DEFAULT_TIME,
    eDate: end.date,
    eTime: end.time,
    reminders: [DEFAULT_REMINDER],
    recur: emptyRecurrenceForm(),
  };
}

export function EventModal({
  open,
  onClose,
  calendars,
  editing,
  scope,
  day,
}: {
  open: boolean;
  onClose: () => void;
  calendars: Calendar[];
  editing: CalendarEvent | null;
  /** Fixed once, when the modal is opened (see CalendarPage) — "this" edits
   *  just the clicked occurrence/override; "all" edits the series master.
   *  Irrelevant for a plain (non-series) event. */
  scope: "this" | "all";
  day: Date;
}) {
  const tz = useTz();
  const create = useCreateEvent();
  const update = useUpdateEvent();
  const del = useDeleteEvent();
  const confirm = useConfirm();

  const isSeries = editing?.recurrenceRule != null || editing?.masterEventId != null;
  const masterId = editing ? (editing.masterEventId ?? editing.id) : null;
  // "All events" edits the true series master (its own stored DTSTART/rule) —
  // fetch it when needed, falling back to `editing` while it loads. "This
  // event" edits the clicked occurrence/override directly — no fetch needed.
  const { data: master } = useEvent(isSeries && scope === "all" ? masterId : null);
  const sourceEvent = isSeries && scope === "all" ? (master ?? editing) : editing;
  const zone = editZone(sourceEvent, tz);

  const {
    form: f,
    patch,
    saving,
    deleting,
    submit: hookSubmit,
    remove,
    requestClose: hookRequestClose,
    discard,
  } = useEntityForm<CalendarEvent, FormState>({
    open,
    editing: sourceEvent,
    initial: (ev) => initialForm(ev, day, calendars, tz),
    onClose,
    save: submit,
    remove: doDelete,
  });

  // Every close path — backdrop, Escape, Cancel — goes through here, same
  // "Discard changes?" wording every other entity modal in the app uses.
  const requestClose = useConfirmedClose(hookRequestClose, discard, "this event");

  const patchRecur = (p: Partial<RecurrenceForm>) => patch({ recur: { ...f.recur, ...p } });

  function askDelete() {
    if (!editing || masterId == null || !remove) return;
    const wholeSeries = isSeries && scope === "all";
    return confirm({
      title: "Delete event",
      message: (
        <>
          Delete <span className="font-bold text-ink">“{editing.title}”</span>
          {wholeSeries ? " — this deletes every event in the series" : ""}? This can’t be undone.
        </>
      ),
      action: remove,
    });
  }

  // `ev` is always the occurrence/master being edited here — `remove` is
  // undefined (and this is never called) in create mode.
  async function doDelete(ev: CalendarEvent) {
    if (masterId == null) return;
    if (isSeries && scope === "this") {
      await del.mutateAsync({
        id: masterId,
        scope: "this",
        occurrenceStart: ev.recurrenceId ?? ev.start,
      });
    } else {
      await del.mutateAsync({ id: masterId });
    }
  }

  const durMins = Math.max(0, minutesBetween(f.sDate, f.sTime, f.eDate, f.eTime));
  const durOptions =
    DURATION_OPTIONS.includes(durMins) || durMins <= 0
      ? DURATION_OPTIONS
      : [durMins, ...DURATION_OPTIONS];

  // Changing the start preserves the current duration (end shifts with it).
  function setStartDate(date: string) {
    const e = addMinutesToDateTime(date, f.sTime, Math.max(15, durMins));
    patch({ sDate: date, eDate: e.date, eTime: e.time });
  }
  function setStartTime(time: string) {
    const e = addMinutesToDateTime(f.sDate, time, Math.max(15, durMins));
    patch({ sTime: time, eDate: e.date, eTime: e.time });
  }
  function setDuration(mins: number) {
    const e = addMinutesToDateTime(f.sDate, f.sTime, mins);
    patch({ eDate: e.date, eTime: e.time });
  }

  // The Save button's `disabled={!f.title.trim()}` already keeps this from
  // being reached with a blank title via a click; `onTitleKeyDown` below
  // guards the Enter-key path the same way, since that bypasses the button.
  async function submit(form: FormState, ev: CalendarEvent | null) {
    // All-day events are stored as UTC-midnight of their civil date (read back
    // verbatim, zone-free) with no origin zone; timed events resolve their
    // wall-clock in the origin zone. See docs/timezones.md.
    const start = form.allDay
      ? `${form.sDate}T00:00:00.000Z`
      : combineDateTime(form.sDate, form.sTime, zone);
    // keep the end (incl. multi-day all-day) so events span correctly
    const end = form.allDay
      ? `${form.eDate}T00:00:00.000Z`
      : combineDateTime(form.eDate, form.eTime, zone);
    const basePatch = {
      calendarId: form.calendarId,
      title: form.title.trim(),
      description: form.description.trim() || null,
      location: form.location.trim() || null,
      start,
      end,
      allDay: form.allDay,
      reminders: form.reminders,
    };
    if (ev && masterId != null) {
      if (isSeries && scope === "this") {
        // An override never carries its own rule or origin zone — omitted here
        // regardless of what the (hidden) Repeat UI last held.
        await update.mutateAsync({
          id: masterId,
          patch: basePatch,
          scope: "this",
          occurrenceStart: ev.recurrenceId ?? ev.start,
        });
      } else {
        const recurrenceRule = recurrenceFormToRule(form.recur);
        const timezone = form.allDay ? null : zone;
        await update.mutateAsync({
          id: masterId,
          patch: { ...basePatch, recurrenceRule, timezone },
        });
      }
    } else {
      const recurrenceRule = recurrenceFormToRule(form.recur);
      const timezone = form.allDay ? null : zone;
      await create.mutateAsync({
        ...basePatch,
        description: form.description.trim() || undefined,
        location: form.location.trim() || undefined,
        end: end ?? undefined,
        recurrenceRule: recurrenceRule ?? undefined,
        timezone: timezone ?? undefined,
        tags: [],
      });
    }
  }

  const dateField = cx("mono", inputBase, "min-w-0 flex-1");
  const timeField = cx("mono", inputBase, "w-28 flex-none");

  function onTitleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      e.preventDefault();
      if (f.title.trim()) hookSubmit();
    }
  }

  return (
    <>
      <Modal open={open} onClose={requestClose} title={editing ? "Edit event" : "New event"}>
        <datalist id="wp-time-options">
          {TIME_OPTIONS.map((t) => (
            <option key={t} value={t} />
          ))}
        </datalist>
        <div className="space-y-3.5">
          <Field label="Title">
            <input
              className={inputClass}
              value={f.title}
              autoFocus
              onChange={(e) => patch({ title: e.target.value })}
              onKeyDown={onTitleKeyDown}
              placeholder="Event name"
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Calendar">
              <select
                className={inputClass}
                value={f.calendarId}
                onChange={(e) => patch({ calendarId: Number(e.target.value) })}
              >
                {calendars.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Location">
              <input
                className={inputClass}
                value={f.location}
                onChange={(e) => patch({ location: e.target.value })}
                placeholder="Optional"
              />
            </Field>
          </div>

          <label className="flex items-center justify-between rounded-lg border border-line bg-surface-2 px-3 py-2.5">
            <span className="text-sm font-semibold text-ink-soft">All-day</span>
            <input
              type="checkbox"
              checked={f.allDay}
              onChange={(e) => patch({ allDay: e.target.checked })}
              className="h-4 w-4 accent-[var(--color-accent)]"
            />
          </label>

          <Field label="Starts">
            <div className="flex gap-2.5">
              <input
                type="date"
                className={dateField}
                value={f.sDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
              {!f.allDay && (
                <input
                  type="time"
                  step={1800}
                  list="wp-time-options"
                  className={timeField}
                  value={f.sTime}
                  onChange={(e) => setStartTime(e.target.value)}
                />
              )}
            </div>
          </Field>

          {/* Once a timed event repeats, the end DATE stops meaning anything (the
            series' own end lives in Repeat below) — but the end TIME still
            does: it's just the occurrence's end-of-day time, i.e. duration,
            same as the Duration control right after it. An all-day event has
            no time/duration at all, so it keeps the full date field
            (relabeled, since "Repeat" now has its own real series-end
            control) to define a multi-day span. */}
          {(f.allDay || f.recur.mode === "none") && (
            <Field label={f.allDay && f.recur.mode !== "none" ? "Spans through" : "Ends"}>
              <div className="flex gap-2.5">
                <input
                  type="date"
                  className={dateField}
                  value={f.eDate}
                  onChange={(e) => patch({ eDate: e.target.value })}
                />
                {!f.allDay && (
                  <input
                    type="time"
                    step={1800}
                    list="wp-time-options"
                    className={timeField}
                    value={f.eTime}
                    onChange={(e) => patch({ eTime: e.target.value })}
                  />
                )}
              </div>
            </Field>
          )}
          {!f.allDay && f.recur.mode !== "none" && (
            <Field label="Ends">
              <input
                type="time"
                step={1800}
                list="wp-time-options"
                className={timeField}
                value={f.eTime}
                onChange={(e) => patch({ eDate: f.sDate, eTime: e.target.value })}
              />
            </Field>
          )}

          {!f.allDay && (
            <div className="flex items-center justify-between gap-3">
              <span className="text-xs font-semibold text-muted">Duration</span>
              <select
                className={cx(inputBase, "w-40")}
                value={durMins}
                onChange={(e) => setDuration(Number(e.target.value))}
              >
                {durOptions.map((m) => (
                  <option key={m} value={m}>
                    {durationLabel(m)}
                    {!DURATION_OPTIONS.includes(m) ? " (custom)" : ""}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* An override never has its own rule — meaningless when editing just
            this occurrence, so the whole control is hidden. */}
          {!(isSeries && scope === "this") && (
            <RecurrenceEditor recur={f.recur} sDate={f.sDate} onChange={patchRecur} />
          )}

          {!f.allDay && zone !== tz && <p className="text-[11px] text-faint">Times in {zone}</p>}

          <TextArea
            label="Description"
            value={f.description}
            onChange={(description) => patch({ description })}
            onEnter={() => f.title.trim() && hookSubmit()}
            placeholder="Add details (optional)"
          />

          <ReminderPicker reminders={f.reminders} onChange={(reminders) => patch({ reminders })} />

          <div className="flex items-center justify-between pt-1">
            <div>
              {editing && (
                <Button variant="danger" onClick={askDelete} disabled={deleting}>
                  Delete
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={requestClose}>
                Cancel
              </Button>
              <Button variant="primary" onClick={hookSubmit} disabled={!f.title.trim() || saving}>
                {editing ? "Save" : "Create"}
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
}
